import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../utils/api";

export default function ShopPerformanceScore({ compact = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(null);
  const [error, setError] = useState(null);
  const [showTips, setShowTips] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const result = await apiGet("/api/shop/performance");
        setScore(result);
      } catch (err) {
        setError(err?.message || "Failed to load performance score");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Score</h3>
        <div className="animate-pulse">
          <div className="h-24 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Score</h3>
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  const overallScore = Number(score?.overallScore || 0);
  const fulfillmentRate = Number(score?.fulfillmentRate || 0);
  const avgShipTime = Number(score?.avgShipTime || 0);
  const customerRating = Number(score?.customerRating || 0);

  const scoreColor =
    overallScore >= 80
      ? "text-emerald-600"
      : overallScore >= 60
      ? "text-yellow-600"
      : "text-red-600";

  const metrics = [
    { label: "Fulfillment Rate", value: `${fulfillmentRate.toFixed(1)}%`, score: fulfillmentRate },
    { label: "Avg. Ship Time", value: `${avgShipTime.toFixed(1)} days`, score: avgShipTime <= 2 ? 100 : avgShipTime <= 5 ? 70 : 40 },
    { label: "Customer Rating", value: `${customerRating.toFixed(1)}/5`, score: (customerRating / 5) * 100 },
  ];

  // Generate actionable tips based on performance
  const tips = [];
  if (fulfillmentRate < 95) {
    tips.push({
      icon: "📦",
      text: "Improve fulfillment rate by shipping orders within 24 hours",
      action: () => navigate("/shop/orders"),
    });
  }
  if (avgShipTime > 3) {
    tips.push({
      icon: "⚡",
      text: "Reduce shipping time to boost customer satisfaction",
      action: () => navigate("/shop/settings"),
    });
  }
  if (customerRating < 4.5) {
    tips.push({
      icon: "⭐",
      text: "Respond to reviews and improve product quality",
      action: () => navigate("/shop/reviews"),
    });
  }
  if (overallScore < 80 && tips.length === 0) {
    tips.push({
      icon: "💡",
      text: "Keep shipping orders quickly and maintain quality",
      action: null,
    });
  }

  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Performance Score</h3>
        {tips.length > 0 && (
          <button
            onClick={() => setShowTips(!showTips)}
            className="text-xs font-semibold text-blue-600 hover:text-blue-700"
          >
            {showTips ? "Hide Tips" : "Show Tips"}
          </button>
        )}
      </div>

      <div className="flex items-center justify-center mb-6">
        <div className="text-center">
          <div className={`text-5xl font-black ${scoreColor}`}>
            {overallScore.toFixed(0)}
          </div>
          <div className="text-sm text-gray-500 mt-1">out of 100</div>
        </div>
      </div>

      {!compact && (
        <div className="space-y-3 pt-4 border-t border-gray-200">
          {metrics.map((metric) => (
            <div key={metric.label}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">{metric.label}</span>
                <span className="text-sm font-semibold text-gray-900">{metric.value}</span>
              </div>
              {/* Progress bar */}
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    metric.score >= 80
                      ? "bg-emerald-500"
                      : metric.score >= 60
                      ? "bg-yellow-500"
                      : "bg-red-500"
                  }`}
                  style={{ width: `${Math.min(metric.score, 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {showTips && tips.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
          <div className="text-xs font-bold text-gray-700 mb-2">💡 Improvement Tips:</div>
          {tips.map((tip, index) => (
            <div
              key={index}
              className={`p-3 rounded-lg bg-blue-50 border border-blue-200 ${
                tip.action ? "cursor-pointer hover:bg-blue-100" : ""
              }`}
              onClick={tip.action}
            >
              <div className="flex items-start gap-2">
                <span className="text-base">{tip.icon}</span>
                <span className="text-xs text-gray-700 flex-1">{tip.text}</span>
                {tip.action && (
                  <span className="text-xs font-semibold text-blue-600">→</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="text-xs text-gray-500">
          {overallScore >= 80
            ? "✓ Excellent performance! Keep it up."
            : overallScore >= 60
            ? "⚠ Good, but there's room for improvement."
            : "⚠ Action needed to improve shop performance."}
        </div>
      </div>
    </div>
  );
}
